import { useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Loader2, Mail, MailCheck, Plus, Send, Trash2 } from 'lucide-react';
import {
  useCreateEmailDestination,
  useDeleteDestination,
  useNotificationDestinations,
  useResendDestinationVerification,
  useTestDestination,
  type NotificationDestination,
} from '@/hooks/useNotifications.ts';
import { useConfirm } from '@/hooks/useConfirm.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
// Through the barrel, never `./PushEnrollment.tsx` — the trial build aliases this
// specifier to a teaser, and a direct import would ship the real push code (and fail
// `check-trial-bundle.mjs` on `pushManager`).
import { PushEnrollment } from '@/features/webPush';

/** A 429 is the per-destination cooldown, not a broken address. */
function isRateLimited(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 429;
}

/** The backend's 400s are already written for a human; anything else is ours to phrase. */
function createErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.status === 400) {
    const detail: unknown = error.response.data;
    if (typeof detail === 'string' && detail.length > 0) return detail;
    if (detail && typeof detail === 'object' && 'error' in detail && typeof detail.error === 'string') {
      return detail.error;
    }
    return "That address wasn't accepted.";
  }
  return "Couldn't add that address.";
}

/**
 * One email destination.
 *
 * An unverified row offers no test button. The API would answer it politely — a
 * non-delivery with "check your verification email" rather than an error — but a
 * control whose only outcome is a refusal teaches people the feature is broken. The
 * state it is actually in, and the one action that moves it forward, is the email
 * already sitting in their inbox.
 */
function EmailRow({ destination }: { destination: NotificationDestination }) {
  const test = useTestDestination();
  const resend = useResendDestinationVerification();
  const remove = useDeleteDestination();
  const { confirm, confirmDialog } = useConfirm();

  // Minted from a verified identity email (§5.2 grandfathering) rather than confirmed
  // here — worth saying, so nobody hunts for an email they were never sent.
  const fromIdentity = destination.verifiedVia === 'identity_email';

  const onRemove = async () => {
    const ok = await confirm({
      title: 'Remove this destination?',
      message: `${destination.target} will stop receiving notifications. Rules pointing at it will fall back to your account email.`,
      confirmText: 'Remove',
      isDanger: true,
    });
    if (ok) remove.mutate(destination.id);
  };

  return (
    <div className="rounded-md border border-border-subtle px-3 py-2 space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm min-w-0">
          {destination.verified ? (
            <MailCheck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          ) : (
            <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <span className="truncate">{destination.target}</span>
          {fromIdentity ? (
            <Badge className="bg-muted text-muted-foreground border-0 font-normal">Account email</Badge>
          ) : null}
        </span>
        <span className="flex items-center gap-1.5 shrink-0">
          {destination.verified ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => { test.mutate(destination.id); }}
              disabled={test.isPending}
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {test.isPending ? 'Sending…' : 'Send test'}
            </Button>
          ) : null}
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => { void onRemove(); }}
            disabled={remove.isPending}
            aria-label={`Remove ${destination.target}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </span>
      </div>

      {destination.verified ? null : (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">
            Waiting for confirmation — open the link we emailed to {destination.target}. Nothing is sent here
            until you do.
          </p>
          {/* The token only ever arrives by email, so a lost message would otherwise
              leave delete-and-re-add as the only way forward. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => { resend.mutate(destination.id); }}
            disabled={resend.isPending}
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            {resend.isPending ? 'Sending…' : 'Resend confirmation'}
          </Button>
          {resend.isSuccess ? <p className="text-xs text-muted-foreground">Sent. The newest link is the one that works.</p> : null}
          {resend.isError ? (
            <p className="text-xs text-destructive">
              {isRateLimited(resend.error) ? 'Just sent one — give it a minute.' : "Couldn't resend the confirmation."}
            </p>
          ) : null}
        </div>
      )}

      {/* A failure streak is the only signal a destination is quietly rotting; the
          router does not disable it (§7.10 rules out circuit breaking), so saying so
          is the whole remedy. */}
      {destination.verified && destination.failureCount > 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {destination.failureCount === 1
            ? 'The last delivery here failed.'
            : `The last ${String(destination.failureCount)} deliveries here failed.`}
        </p>
      ) : null}

      {test.isSuccess && !test.data.delivered ? (
        <p className="text-xs text-destructive">{test.data.detail ?? "The test couldn't be delivered."}</p>
      ) : null}
      {test.isSuccess && test.data.delivered ? (
        <p className="text-xs text-muted-foreground">Test sent. It should arrive in a moment.</p>
      ) : null}
      {test.isError ? (
        <p className="text-xs text-destructive">
          {isRateLimited(test.error) ? 'Just tested — give it a minute before trying again.' : "Couldn't send a test."}
        </p>
      ) : null}

      {confirmDialog}
    </div>
  );
}

/** Add an address. It is created unverified; the confirmation email does the rest. */
function AddEmail() {
  const [address, setAddress] = useState('');
  const create = useCreateEmailDestination();

  const submit = () => {
    const trimmed = address.trim();
    if (!trimmed) return;
    create.mutate(trimmed, { onSuccess: () => { setAddress(''); } });
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Input
          type="email"
          value={address}
          placeholder="you@example.com"
          className="h-8 text-sm"
          onChange={(e) => { setAddress(e.target.value); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          aria-label="Email address to add"
        />
        <Button size="sm" onClick={submit} disabled={create.isPending || !address.trim()}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          {create.isPending ? 'Adding…' : 'Add'}
        </Button>
      </div>
      {create.isError ? <p className="text-xs text-destructive">{createErrorMessage(create.error)}</p> : null}
      {/* No success message: the row that just appeared already says a confirmation is
          pending, and says it about a named address. Two statements of the same fact,
          one of them vaguer, is worse than one. */}
    </div>
  );
}

/**
 * Where a rule can send (§5.2).
 *
 * Until this existed there was no destinations surface at all: push enrolment was
 * the only destination-shaped UI in the product, so a rule could name an email
 * channel while the account owned no address the router would accept. Email and
 * push sit together here because a rule picks between them, and a list of one kind
 * reads as though the other does not exist.
 *
 * Personal destinations are email and push only. The webhook-shaped kinds are
 * organization-owned by design — a personal Slack webhook is a shared channel one
 * user could point at without anyone else knowing — so they belong to a surface
 * gated on `notification_route:manage`, not to this one.
 */
export function NotificationDestinations() {
  const { data: destinations, isLoading } = useNotificationDestinations();

  const emails = (destinations ?? []).filter((d) => d.kind === 'email');

  return (
    <section className="bg-card border border-border rounded-lg p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold">Notification destinations</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Where your notification rules can send. Your account email is here already; add another address to route
          somewhere else.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</h3>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {emails.map((d) => (
              <EmailRow key={d.id} destination={d} />
            ))}
            <AddEmail />
          </>
        )}
      </div>

      <PushEnrollment />
    </section>
  );
}
